// src/Welcome.js — the one-time welcome for new accounts (approved mockup: "FriSo Onboarding" artifact,
// 2026-10-12). Three steps, each skippable: what FriSo is; join the pod you were invited to (or make a first
// pod); connect Google Calendar and pick which calendars show. Calendar.js decides when it shows and owns all
// the data and saving; this is display plus a little local state. Someone already in a pod (e.g. they joined their
// invite, then left before finishing) skips the pod step.
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import BrandLogo from "./BrandLogo";
import { getInitials } from "./dateUtils";

const POD_IDEAS = ["Family", "Roommates", "Close friends", "Book club"];
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

function Steps({ step }) {
  return (
    <span className="welcome__steps" aria-label={`Step ${step} of 3`}>
      {[1, 2, 3].map((n) => <i key={n} className={n === step ? "is-on" : n < step ? "is-done" : ""} />)}
    </span>
  );
}

export default function Welcome({
  firstName, hasPod, startStep = 1, pendingInvitations, onAcceptInvitation, onDeclineInvitation, onCreatePod,
  googleConnected, googleCalendars, isCalShown, onToggleCalendar, onConnectGoogle, onDone,
}) {
  const [step, setStep] = useState(startStep);
  const [makeOwn, setMakeOwn] = useState(false); // invited, but chose "Start your own pod instead"
  const [podName, setPodName] = useState("");
  const [invites, setInvites] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const invite = !makeOwn ? pendingInvitations[0] : null;

  // Phones: swipe left = next step (same as "Let's get started" / Skip on step 2), swipe right = back.
  // The last step has no swipe forward, so finishing is always a tap.
  const [dir, setDir] = useState("next");
  const swipe = useRef(null);
  const next = () => { setError(""); setDir("next"); setStep(step === 1 && hasPod ? 3 : step + 1); };
  const back = () => { setError(""); setDir("back"); setStep(step === 3 && hasPod ? 1 : step - 1); };
  const onTouchStart = (e) => { const t = e.touches[0]; swipe.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const s = swipe.current; swipe.current = null;
    if (!s || busy) return;
    const t = e.changedTouches[0], dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return; // too short or mostly up/down
    if (dx < 0 && step < 3) next();
    else if (dx > 0 && step > 1) back();
  };

  const addDraft = () => {
    const parts = draft.split(/[\s,;]+/).filter(Boolean);
    const bad = parts.filter((p) => !isEmail(p));
    const good = parts.filter(isEmail).map((p) => p.toLowerCase()).filter((p) => !invites.includes(p));
    if (good.length) setInvites([...invites, ...good]);
    setDraft(bad.join(" "));
    setError(bad.length ? "That doesn't look like an email address." : "");
  };

  const join = async () => {
    setBusy(true);
    await onAcceptInvitation(invite);
    setBusy(false);
    setStep(3);
  };

  const createPod = async () => {
    const name = podName.trim();
    if (!name) { setError("Give your pod a name."); return; }
    const all = [...invites, ...draft.split(/[\s,;]+/).filter(isEmail).map((p) => p.toLowerCase())];
    setBusy(true);
    try {
      await onCreatePod(name, [...new Set(all)]);
      setStep(3);
    } catch (err) {
      setError("Couldn't create the pod. Please try again.");
    }
    setBusy(false);
  };

  return createPortal(
    <div className="welcome-overlay">
      <section className="welcome" data-dir={dir} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} role="dialog" aria-modal="true" aria-label="Welcome to FriSo">
        <div className="welcome__top">
          {step === 1 ? <BrandLogo /> : (
            <button type="button" className="welcome__back" onClick={back}>‹ Back</button>
          )}
          <Steps step={step} />
        </div>

        {step === 1 && (
          <>
            <div className="welcome__head">
              <h1>{firstName ? `Welcome, ${firstName}` : "Welcome to FriSo"}</h1>
              <p>FriSo is a calendar you share with your people. Make a pod for each group, like your family, roommates or book club, and plan together.</p>
            </div>
            <ul className="welcome__points">
              <li><span className="welcome__icon welcome__icon--mint"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5" /><circle cx="17" cy="7" r="2.5" /><path d="M16.5 13.2c2.6.1 4.4 1.9 5 4.8" /></svg></span>
                <span><b>Share plans with a pod</b>Everyone in it sees what's coming up.</span></li>
              <li><span className="welcome__icon welcome__icon--grey"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="10" width="16" height="10" rx="2.5" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg></span>
                <span><b>Keep private things private</b>Mark a plan Busy and your pods only see the time.</span></li>
              <li><span className="welcome__icon welcome__icon--pink"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg></span>
                <span><b>See who's free</b>Open a pod to see who's free this week and pick a time that works.</span></li>
              <li><span className="welcome__icon welcome__icon--lilac"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 0113.7-5.6L20 8.5M20 4v4.5h-4.5M20 12a8 8 0 01-13.7 5.6L4 15.5M4 20v-4.5h4.5" /></svg></span>
                <span><b>Set your usual busy times</b>Add a schedule, like work 9–5, from your Dashboard. Pods see “Busy”, and it counts when finding a time.</span></li>
            </ul>
            <div className="welcome__foot">
              <button type="button" className="welcome__skip" onClick={onDone}>Skip for now</button>
              <button type="button" className="btn btn--primary welcome__go welcome__start" onClick={next}>Let's get started</button>
              <button type="button" className="welcome__swipe" onClick={next}>Swipe to get started <span aria-hidden="true">›››</span></button>
            </div>
          </>
        )}

        {step === 2 && invite && (
          <>
            <div className="welcome__head">
              <h1>Your friends are waiting</h1>
              <p>Join to see what they're planning and share your own plans with them.</p>
            </div>
            <div className="welcome__invite">
              <div className="welcome__invite-pod">
                <span className="welcome__bubble">{getInitials(invite.podName || "")}</span>
                <span><b>{invite.podName}</b>{invite.inviterEmail} invited you</span>
              </div>
              <div className="welcome__invite-actions">
                <button type="button" className="btn btn--primary welcome__go" disabled={busy} onClick={join}>{busy ? "Joining…" : `Join ${invite.podName}`}</button>
                <button type="button" className="btn btn--secondary welcome__go" disabled={busy} onClick={async () => { await onDeclineInvitation(invite); next(); }}>Not now</button>
              </div>
            </div>
            {pendingInvitations.length > 1 && <p className="welcome__note">You have {pendingInvitations.length - 1} more {pendingInvitations.length === 2 ? "invitation" : "invitations"} on your Dashboard.</p>}
            <div className="welcome__foot">
              <button type="button" className="welcome__link" onClick={() => setMakeOwn(true)}>+ Start your own pod instead</button>
              <button type="button" className="welcome__skip" onClick={next}>Next ›</button>
            </div>
          </>
        )}

        {step === 2 && !invite && (
          <>
            <div className="welcome__head">
              <h1>Make your first pod</h1>
              <p>A pod is a small group you share plans with. You can make more later.</p>
            </div>
            <label className="welcome__field">
              <span>Pod name</span>
              <input className="form-input" value={podName} maxLength={60} placeholder="e.g. Smith Family"
                onChange={(e) => { setPodName(e.target.value); setError(""); }} />
            </label>
            <div className="welcome__ideas" role="group" aria-label="Ideas">
              {POD_IDEAS.map((idea) => <button key={idea} type="button" onClick={() => setPodName(idea)}>{idea}</button>)}
            </div>
            <label className="welcome__field">
              <span>Invite people <small>(optional)</small></span>
              <div className="welcome__emails">
                {invites.map((em) => (
                  <span key={em} className="welcome__chip">{em}<button type="button" aria-label={`Remove ${em}`} onClick={() => setInvites(invites.filter((x) => x !== em))}>×</button></span>
                ))}
                <input type="email" value={draft} placeholder={invites.length ? "" : "Their email"} autoComplete="off"
                  onChange={(e) => setDraft(e.target.value)} onBlur={() => draft.trim() && addDraft()}
                  onKeyDown={(e) => { if (["Enter", ",", " "].includes(e.key) && draft.trim()) { e.preventDefault(); addDraft(); } }} />
              </div>
              <small className="welcome__hint">They'll see the invite when they sign in to FriSo with that email.</small>
            </label>
            {error && <p className="auth-error" role="alert">{error}</p>}
            <div className="welcome__foot">
              <button type="button" className="welcome__skip" onClick={next}>Skip for now</button>
              <button type="button" className="btn btn--primary welcome__go" disabled={busy} onClick={createPod}>{busy ? "Creating…" : "Create pod"}</button>
            </div>
          </>
        )}

        {step === 3 && googleCalendars.length > 0 && (
          <>
            <div className="welcome__head">
              <h1>Which calendars should FriSo show?</h1>
              <p>We turned on the ones you use in Google Calendar. Only you see these events, unless you share one with a pod.</p>
            </div>
            <div className="gcal-list" role="group" aria-label="Google calendars">
              {googleCalendars.map((c) => (
                <button key={c.id} type="button" className="gcal-row" role="switch" aria-checked={isCalShown(c.id)} onClick={() => onToggleCalendar(c.id)}>
                  <i style={{ background: c.color || "var(--google-accent)" }} />
                  <span className="gcal-row__name">{c.name}{c.primary && <small>Main calendar</small>}</span>
                  <span className="gcal-row__switch" aria-hidden="true" />
                </button>
              ))}
            </div>
            <div className="welcome__foot">
              <span className="welcome__note">Change this anytime on the Calendar page.</span>
              <button type="button" className="btn btn--primary welcome__go" onClick={onDone}>Done</button>
            </div>
          </>
        )}

        {step === 3 && googleCalendars.length === 0 && (
          <>
            <div className="welcome__head">
              <h1>Bring your Google Calendar</h1>
              <p>See your Google events next to your pods' plans. Only you see them, unless you choose to share one.</p>
            </div>
            {googleConnected ? <p className="welcome__note">Loading your calendars…</p> : (
              <button type="button" className="btn btn--secondary welcome__go welcome__connect" onClick={onConnectGoogle}>Connect Google Calendar</button>
            )}
            <div className="welcome__foot">
              <span className="welcome__note">You can connect it later from your account menu.</span>
              <button type="button" className="btn btn--primary welcome__go" onClick={onDone}>{googleConnected ? "Done" : "Maybe later"}</button>
            </div>
          </>
        )}
      </section>
    </div>,
    document.querySelector(".app-shell") || document.body
  );
}
